import type { SearchBoxSuggestion } from "@mapbox/search-js-core";
import { t } from "@/i18n-js/instance";
import { useButtonVariant } from "@/react/helpers/useButtonVariant";
import { useFilterContext } from "@circle-react-shared/FilterV3/FilterContext";
import type { BaseFilter } from "@circle-react-shared/MemberFilters/types";
import { Button } from "@circle-react-uikit/Button";
import { Form } from "@circle-react-uikit/Form";
import { LocationInput } from "../../uikit/Form/LocationInput";
import { BaseFilterFields } from "./BaseFilterFields";

export interface LocationData {
  id: string;
  name: string;
  source: string;
}

interface LocationSubmitObject {
  data?: SearchBoxSuggestion;
  text?: string;
  type?: string;
  value?: LocationData;
}

interface FormSubmitData {
  // Because how Form's types work, we can't just use LocationSubmitObject
  // and have it work with the dynamic name from the component prop.
  [dynamicKey: string]: string | LocationSubmitObject | undefined;
  id: string;
  key: string;
}

export interface MapBoxFilterParams extends BaseFilter {
  name?: string;
  value?: LocationData;
}

export function MapboxInputFilter({
  param,
  name,
}: {
  param: MapBoxFilterParams;
  name: string;
}) {
  const { onApply } = useFilterContext();
  const primaryButtonVariant = useButtonVariant();

  const defaultValues: FormSubmitData = {
    id: param.id,
    key: name,
    [name]: {
      value: param.value,
    },
  };

  const handleApply = (submitData: FormSubmitData) => {
    const locationData = submitData[name];

    // Type guard is essential because submitData[name] could be string | LocationSubmitObject | undefined
    // since we're using the dynamic name from the component prop to access the correct property
    if (
      typeof locationData !== "object" ||
      locationData === null ||
      !locationData.data
    ) {
      return;
    }

    const { mapbox_id, name: locationName } = locationData.data;
    if (mapbox_id && locationName) {
      onApply({
        id: submitData.id,
        key: submitData.key,
        value: {
          id: mapbox_id,
          name: locationName,
          source: "mapbox_v6",
        },
      });
    }
  };

  return (
    <div className="flex flex-col gap-y-3 py-4">
      <Form defaultValues={defaultValues} onSubmit={handleApply}>
        <div className="flex flex-col gap-y-4 px-4">
          <LocationInput
            name={name}
            shouldSkipLocationDetailRetrieve
            initialValue={param.value}
            shouldShowClearButton={false}
          />

          <BaseFilterFields />

          <Button variant={primaryButtonVariant} type="submit" full>
            {t("filters.done")}
          </Button>
        </div>
      </Form>
    </div>
  );
}
